import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { APP_LANGUAGES } from '@core/constants/app-languages';
import { AppTranslateLanguageService } from '@core/services/app-translate-language.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

export interface AuthLang {
  label: string;
  value: string;
  flag: string;
}

@Component({
  selector: 'app-auth',
  standalone: true,
  imports: [RouterOutlet, TranslateModule],
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
})
export class AuthComponent {
  readonly year = new Date().getFullYear();

  private readonly translate = inject(TranslateService);
  private readonly langService = inject(AppTranslateLanguageService);
  private readonly router = inject(Router);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url)
    ),
    { initialValue: this.router.url }
  );

  /** Show language selector only on the working period (select-company) page. */
  readonly showLocale = computed(() => this.url().includes('/auth/select-company'));

  readonly languageOptions = APP_LANGUAGES.map(({ labelKey, value, emoji }) => ({
    labelKey,
    value,
    flag: emoji,
  }));

  /** Invalidates `languages` / `currentLang` when i18n language changes. */
  private readonly langTick = signal(0);

  constructor() {
    this.translate.onLangChange.subscribe(() => this.langTick.update((n) => n + 1));
  }

  readonly languages = computed((): AuthLang[] => {
    this.langTick();
    return this.languageOptions.map((opt) => ({
      label: this.translate.instant(opt.labelKey),
      value: opt.value,
      flag: opt.flag,
    }));
  });

  readonly currentLang = computed((): AuthLang => {
    this.langTick();
    const cur = this.translate.getCurrentLang();
    const opt = this.languageOptions.find((l) => l.value === cur) ?? this.languageOptions[0];
    return {
      label: this.translate.instant(opt.labelKey),
      value: opt.value,
      flag: opt.flag,
    };
  });

  selectLang(value: string): void {
    this.langService.useLanguage(value);
    this.langTick.update((n) => n + 1);
  }
}
